import type { FC } from 'react';
import { useNavigate } from 'react-router';
import { ownerContributorAndViewerIds } from 'src/components/form';
import { useI18NSummaryHelpContent } from 'src/components/help-panel/useSummaryHelpContent';
import { useInsertQuestionnaireTemplate } from 'src/hooks/mutations/questionnaire-template';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useIsFormPrefillEnabled } from '@/hooks/useIsFormPrefillEnabled';
import {
  questionnaireTemplateDetailsUrl,
  questionnaireTemplateVersionCreateUrl,
} from '@/utils/urls';

import { QuestionnaireTemplateForm } from '../../forms/QuestionnaireTemplateForm';
import type { QuestionnaireTemplateFormData } from '../../forms/questionnaireTemplateSchema';

const QuestionnaireTemplateCreateTab: FC = () => {
  useI18NSummaryHelpContent('questionnaire_templates.help');
  const navigate = useNavigate();
  const { insertQuestionnaireTemplate } = useInsertQuestionnaireTemplate();
  const objectToolbarEnabled = useIsFeatureFlagEnabled('dynamic_forms');
  const formPrefillEnabled = useIsFormPrefillEnabled();

  const onSave = async (data: QuestionnaireTemplateFormData) => {
    const result = await insertQuestionnaireTemplate({
      variables: {
        object: {
          CustomAttributeData: data.CustomAttributeData || undefined,
          DepartmentTypeIds:
            data.departments?.map((d) => d.DepartmentTypeId) || [],
          TagTypeIds: data.tags?.map((t) => t.TagTypeId) || [],
          Description: data.Description,
          Title: data.Title,
          ...ownerContributorAndViewerIds(data),
        },
      },
    });

    const questionnaireTemplateId = result?.insertQuestionnaireTemplateApi?.Id;
    if (!questionnaireTemplateId) {
      throw new Error('Questionnaire template id is missing');
    }

    return {
      savedIds: [questionnaireTemplateId],
      createdEntity: {
        id: questionnaireTemplateId,
        // Recovery skips version creation because that follow-up step did
        // not complete; the persisted template itself is the safe target.
        destination: questionnaireTemplateDetailsUrl(questionnaireTemplateId),
      },
      successNavigation: {
        destination: questionnaireTemplateVersionCreateUrl(
          questionnaireTemplateId
        ),
        isReplace: true,
      },
    };
  };

  const onDismiss = (saved?: boolean) => {
    if (!saved) {
      navigate(-1);
    }
  };

  return (
    <QuestionnaireTemplateForm
      onSave={onSave}
      onDismiss={onDismiss}
      publishToolbarState={objectToolbarEnabled}
      publishActiveForm={formPrefillEnabled}
    />
  );
};

export default QuestionnaireTemplateCreateTab;
